// =================================================================================
// File: src/pages/SelectRolePage.jsx
// Description: Role selection page - choose between Visitor/Explorer or Studio/Maker
// Users select how they want to use Mudo, choice is saved to localStorage
// =================================================================================

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * SelectRolePage - Entry point for users to choose their app experience
 * 
 * Flow:
 * 1. On mount, check localStorage for existing role choice
 *    - If "studio" → auto-redirect to /dashboard
 *    - If "visitor" → auto-redirect to /discover
 *    - If none → show selection UI
 * 
 * 2. User selects a role card
 *    - Save choice to localStorage as "mudo:userRole"
 *    - Navigate to appropriate route
 * 
 * Roles:
 * - "visitor" → Public discovery: world map, workshops, marketplace (/discover)
 * - "studio" → Studio dashboard: manage workshops, bookings, inventory (/dashboard)
 */
export default function SelectRolePage() {
  const navigate = useNavigate();

  // Check for existing role preference on mount
  useEffect(() => {
    const savedRole = localStorage.getItem("mudo:userRole");
    
    if (savedRole === "studio") {
      navigate("/dashboard", { replace: true });
    } else if (savedRole === "visitor") {
      navigate("/discover", { replace: true });
    }
    // If no saved role, stay on this page to show selection UI
  }, [navigate]);

  /**
   * Handle role selection
   * @param {string} role - Either "visitor" or "studio"
   */
  const handleRoleSelect = (role) => {
    // Save choice to localStorage
    localStorage.setItem("mudo:userRole", role);
    
    // Navigate to appropriate route
    if (role === "visitor") {
      navigate("/discover");
    } else if (role === "studio") {
      navigate("/dashboard/home");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden">
      {/* Subtle background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-950/20 via-slate-950 to-orange-950/20" />
      
      {/* Main content container */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12 w-full">
        {/* Page header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Who are you today?
          </h1>
          <p className="text-lg text-white/70">
            Choose how you'd like to use Mudo right now.
          </p>
        </div>

        {/* Role selection cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Visitor / Explorer */}
          <div
            className="group cursor-pointer"
            onClick={() => handleRoleSelect("visitor")}
          >
            <div className="h-full bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-[0_18px_45px_rgba(0,0,0,0.45)] p-6 sm:p-8 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_60px_rgba(0,0,0,0.55)] hover:bg-white/8">
              {/* Icon */}
              <div className="text-6xl mb-2">🧭</div>
              
              {/* Title */}
              <h2 className="text-2xl font-bold text-white">
                I'm here to explore
              </h2>
              
              {/* Description */}
              <p className="text-white/70 leading-relaxed flex-1">
                Find studios, workshops, and spaces near you. Book directly
                through Mudo when you're ready.
              </p>
              
              {/* Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRoleSelect("visitor");
                }}
                className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg hover:shadow-xl hover:from-amber-400 hover:to-orange-400 transition-all duration-200 group-hover:scale-[1.02]"
              >
                Continue as Visitor
                <svg
                  className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
              
              {/* Decorative features list */}
              <div className="mt-2 space-y-1 text-xs text-white/50">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-amber-500/50" />
                  Browse global studio map
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-amber-500/50" />
                  Discover workshops & classes
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-amber-500/50" />
                  Book studio space
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Studio / Maker */}
          <div
            className="group cursor-pointer"
            onClick={() => handleRoleSelect("studio")}
          >
            <div className="h-full bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl shadow-[0_18px_45px_rgba(0,0,0,0.45)] p-6 sm:p-8 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_60px_rgba(0,0,0,0.55)] hover:bg-white/8">
              {/* Icon */}
              <div className="text-6xl mb-2">🏺</div>
              
              {/* Title */}
              <h2 className="text-2xl font-bold text-white">
                I run a studio or brand
              </h2>
              
              {/* Description */}
              <p className="text-white/70 leading-relaxed flex-1">
                Access your dashboard, manage workshops, studio time, and
                bookings—all in one place.
              </p>
              
              {/* Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRoleSelect("studio");
                }}
                className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg hover:shadow-xl hover:from-orange-500 hover:to-red-500 transition-all duration-200 group-hover:scale-[1.02]"
              >
                Continue as Studio
                <svg
                  className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
              
              {/* Decorative features list */}
              <div className="mt-2 space-y-1 text-xs text-white/50">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-orange-500/50" />
                  Manage inventory & production
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-orange-500/50" />
                  Schedule workshops & bookings
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-orange-500/50" />
                  Track costs & analytics
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center mt-8 text-sm text-white/50">
          Your choice is saved. You can change it anytime from settings.
        </div>
      </div>
    </div>
  );
}
